import { useMutation } from '@tanstack/react-query';
import { hasRole } from '@lb-classroom/feature-auth';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useDispatch } from 'react-redux';
import { loginSucceeded } from '../../../actions/auth';
import { loginWithGoogle } from '../../../api/generated';
import { useFlashMessage } from '../../../hooks/use-flash-message';
import type { AppDispatch } from '../../../store';

type AuthErrorLike = {
  code?: string;
  message?: string;
  error?: string;
  body?: {
    code?: string;
    error?: string;
    message?: string;
  };
};

type GoogleAuthResponse = {
  authToken: string;
  refreshToken?: string;
  expiresIn?: number;
  profile?: string;
  user?: {
    id?: string;
    profile_type?: string;
    profileType?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
};

const FLASH_ERROR_OPTIONS = {
  type: 'error' as const,
  autoDisappear: false,
  sticky: false,
};

const getErrorCode = (error: AuthErrorLike | undefined): string => {
  return (
    error?.code ||
    error?.body?.code ||
    error?.body?.error ||
    error?.error ||
    ''
  ).toLowerCase();
};

const getErrorMessage = (error: AuthErrorLike | undefined): string => {
  return (
    error?.message ||
    error?.body?.message ||
    error?.body?.error ||
    error?.error ||
    ''
  ).toLowerCase();
};

export const mapGoogleAuthErrorKey = (error: unknown): string => {
  const authError = (error || {}) as AuthErrorLike;
  const code = getErrorCode(authError);
  const message = getErrorMessage(authError);

  if (
    code.includes('popup') ||
    message.includes('popup') ||
    message.includes('blocked')
  ) {
    return 'auth.googlePopupBlocked';
  }

  if (
    code.includes('consent') ||
    code.includes('cancelled') ||
    message.includes('consent') ||
    message.includes('cancel')
  ) {
    return 'auth.googleConsentDenied';
  }

  if (code === 'email_not_verified' || message.includes('not verified')) {
    return 'auth.googleEmailNotVerified';
  }

  if (
    code.includes('network') ||
    message.includes('network') ||
    (authError as { name?: string }).name === 'TypeError'
  ) {
    return 'auth.networkError';
  }

  return 'auth.googleAuthError';
};

const normalizeAuthPayload = (payload: GoogleAuthResponse) => {
  const profile =
    payload.profile ||
    payload.user?.profile_type ||
    payload.user?.profileType ||
    '';
  const user = payload.user
    ? {
        ...payload.user,
        profile_type: payload.user.profile_type || payload.user.profileType,
      }
    : undefined;

  return {
    ...payload,
    profile,
    user,
  };
};

const persistAuthData = (payload: ReturnType<typeof normalizeAuthPayload>) => {
  if (typeof localStorage === 'undefined') {
    return;
  }

  localStorage.setItem('authToken', payload.authToken);

  if (payload.refreshToken) {
    localStorage.setItem('refreshToken', payload.refreshToken);
  }

  if (payload.profile) {
    localStorage.setItem('profile', payload.profile);
  }

  if (payload.user) {
    localStorage.setItem('user', JSON.stringify(payload.user));
  }

  if (payload.user?.id) {
    localStorage.setItem('userId', String(payload.user.id));
  }

  if (payload.expiresIn) {
    const expiresAt = Date.now() + payload.expiresIn * 1000;
    localStorage.setItem('tokenExpiresAt', expiresAt.toString());
  }
};

export const useGoogleOAuthService = () => {
  const dispatch = useDispatch<AppDispatch>();
  const router = useRouter();
  const { addFlash, clearFlash } = useFlashMessage();
  const { t } = useTranslation('common');

  const showError = (error: unknown) => {
    addFlash(t(mapGoogleAuthErrorKey(error)), FLASH_ERROR_OPTIONS);
  };

  const mutation = useMutation({
    mutationFn: async (idToken: string) => {
      const { data } = await loginWithGoogle({
        body: {
          id_token: idToken,
        },
        throwOnError: true,
      });
      return data as GoogleAuthResponse;
    },
    onSuccess: (response) => {
      clearFlash();

      const payload = normalizeAuthPayload(response);
      persistAuthData(payload);
      dispatch(
        loginSucceeded(payload as unknown as Parameters<typeof loginSucceeded>[0]),
      );

      const role = payload.profile || payload.user?.profile_type || '';
      if (hasRole(role, 'Tutor') || role === 'Admin') {
        router.push('/t/dashboard');
        return;
      }

      if (hasRole(role, 'Student')) {
        router.push('/s/dashboard');
        return;
      }

      router.push('/');
    },
    onError: (error) => {
      showError(error);
    },
  });

  return {
    loginWithGoogle: mutation.mutate,
    isLoading: mutation.isPending,
    handleOAuthError: showError,
  };
};
